import { SkeletonComponent } from "@syncfusion/ej2-react-notifications";
import {
  ForwardedRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import PrimaryGridTable, { PrimaryGridTableModule } from "./PrimaryGridTable";
import {
  ApiClient,
  GridColumn,
  GridFilterSettings,
  GridViewPicker,
  PrimaryCard,
  useGridView,
} from "../../../";
import { BaseGridModel } from "../GridTypes/BaseGridModel";
import React from "react";
import { GridHeightSettings } from "../GridSettings/GridHeightSettings";
import { Predicate } from "@syncfusion/ej2-data";

export interface PrimaryGridModule {
  /** Resets the form to its initial state. */
  getGridModule: () => PrimaryGridTableModule;
}

interface PrimaryGridComponentProps<TGridModel extends object, TMetricsModel> {
  gridKey: string;
  apiClient: ApiClient;
  gridConfiguration?: Readonly<BaseGridModel<TGridModel>>;
  columnsConfiguration: GridColumn[];
  getColumnsConfigurationForUser?: () => GridColumn[];
  getAuthRequestHeader: () => Promise<Record<string, string>>;
  odataUrl: string;
  odataParams?: Record<string, string>;
  viewsSettings: {
    // @deprecated - use pathToUserViewApi instead - this never did anything, anyway
    odataUrl?: string;

    // base URL for userview
    pathToUserViewApi?: string;

    gridTitle: string;
    gridAutoName?: string;
    shouldCreateAutoView?: boolean;
    defaultGridViewTitle?: string;
    showGridViewPicker?: boolean;
    showUserDefaultView?: boolean;
  };
  metricsSettings?: {
    url: string;
    columns?: number;
    languageMapping: TMetricsModel;
    showMetricViewPicker?: boolean;
    getAuthRequestHeader?: () => Promise<Record<string, string>>;
  };
  showGridFilters?: boolean;
  allowSearch?: boolean;
  predicate?: Predicate;
  gridHeightSettings?: GridHeightSettings;
  showInCardContainer?: boolean;
  customToolbarComponents?: React.ReactNode;
  leftMenuComponents?: React.ReactNode;
  rightMenuComponents?: React.ReactNode;
  renderGrid?: boolean;
  refreshViewsAfterSaving?: boolean;
}

// Module augmentations allow you to extend the types of existing modules.
// https://www.digitalocean.com/community/tutorials/typescript-module-augmentation#enter-module-augmentation
declare module "react" {
  function forwardRef<T, P = {}>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}
const PrimaryGridComponent = forwardRef(
  <TGridModel extends object, TMetricsModel extends object>(
    props: PrimaryGridComponentProps<TGridModel, TMetricsModel>,
    ref: ForwardedRef<PrimaryGridModule>
  ) => {
    useImperativeHandle(
      ref,
      () =>
        ({
          getGridModule: () => {
            return primaryGridTableModule.current;
          },
        } as PrimaryGridModule)
    );

    const [filterSettings, setFilterSetttings] =
      useState<GridFilterSettings | null>(null);
    const {
      currentGridView,
      currentGridViewKey,
      gridBaseSettings,
      getGridView,
      updateViewColumnSettings,
      setDefaultView,
      baseGridColumnsConfiguration,
      assignCurrentGridView,
      userGridViews,
      updateViewMetricSettings,
    } = useGridView(
      props.gridKey,
      props.viewsSettings.gridTitle,
      props.columnsConfiguration,
      props.getColumnsConfigurationForUser
        ? props.getColumnsConfigurationForUser
        : () => props.columnsConfiguration,
      props.apiClient,
      filterSettings,
      props.viewsSettings.gridAutoName,
      props.viewsSettings.defaultGridViewTitle,
      props.viewsSettings.shouldCreateAutoView,
      props.viewsSettings.pathToUserViewApi,
      false,
      props.refreshViewsAfterSaving
    );

    const primaryGridTableModule = useRef<PrimaryGridTableModule>(null);

    const toolbarItems = useMemo(() => {
      const excelExportItems = props.gridConfiguration?.toolbar?.filter(
        (item) =>
          typeof item === "object" && "id" in item && item.id === "ExcelExport"
      );
      const searchItems = props.gridConfiguration?.toolbar?.filter(
        (item) => typeof item === "object" && !("id" in item)
      );

      if (
        (excelExportItems?.length ?? 0) > 1 ||
        (searchItems?.length ?? 0) > 1
      ) {
        throw new Error(
          "Only one Excel Export and one Search item are allowed in the toolbar"
        );
      }

      const toolbarItemsToReturn =
        props.gridConfiguration?.toolbar?.filter(
          (item) =>
            !excelExportItems?.includes(item) && !searchItems?.includes(item)
        ) ?? [];

      if (props.gridConfiguration?.allowExcelExport) {
        toolbarItemsToReturn.push(
          excelExportItems?.[0] ?? {
            text: "Export to Excel",
            tooltipText: "Export to Excel",
            prefixIcon: "e-upload-2",
            id: "ExcelExport",
          }
        );
      }

      if (props.allowSearch) {
        toolbarItemsToReturn.push(
          searchItems?.[0] ?? {
            text: "Search",
            tooltipText: "Search",
          }
        );
      }

      return toolbarItemsToReturn;
    }, [props.gridConfiguration, props.allowSearch]);

    // Create the grid content that can be conditionally wrapped
    const gridContent = currentGridView && (
      <>
        {!gridBaseSettings && (
          <div>
            <br />
            <SkeletonComponent height="15px"></SkeletonComponent>
          </div>
        )}
        {props.customToolbarComponents && (
          <section className="board-toolbar">
            <ul className="board-toolbar-left">
              <li>{props.customToolbarComponents}</li>
            </ul>
          </section>
        )}
        {gridBaseSettings && (
          <section className="board-toolbar">
            <ul className="board-toolbar-left">
              {props.leftMenuComponents && <li>{props.leftMenuComponents}</li>}
              {props.viewsSettings.showGridViewPicker !== false && (
                <li>
                  <GridViewPicker
                    currentGridView={currentGridView}
                    gridBaseSettings={gridBaseSettings}
                    getGridView={getGridView}
                    updateViewColumnSettings={updateViewColumnSettings}
                    showUserDefaultView={
                      props.viewsSettings.showUserDefaultView
                    }
                    setDefaultView={setDefaultView}
                    baseGridColumnsConfiguration={baseGridColumnsConfiguration}
                    odataUrl={props.viewsSettings.odataUrl}
                    getAuthRequestHeader={props.getAuthRequestHeader}
                    assignCurrentGridView={assignCurrentGridView}
                    userGridViews={userGridViews}
                  />
                </li>
              )}
            </ul>
            {props.rightMenuComponents && (
              <ul className="board-toolbar-right">
                <li>{props.rightMenuComponents}</li>
              </ul>
            )}
          </section>
        )}
        {gridBaseSettings && props.renderGrid !== false && (
          <PrimaryGridTable<TGridModel, TMetricsModel>
            gridKey={props.gridKey}
            gridViewTitle={currentGridView.title}
            key={currentGridViewKey}
            ref={primaryGridTableModule}
            odataUrl={props.odataUrl}
            odataParams={props.odataParams}
            getAuthRequestHeader={props.getAuthRequestHeader}
            metricsSettings={
              props.metricsSettings && {
                ...props.metricsSettings,
                apiClient: props.apiClient,
                metricConfiguration: currentGridView.metricConfiguration,
                updateViewMetricSettings: updateViewMetricSettings,
                viewId: currentGridView.id,
              }
            }
            columnsConfiguration={currentGridView.columnConfiguration}
            filterConfiguration={currentGridView.filterConfiguration}
            sortConfiguration={currentGridView.sortConfiguration}
            onFilterSettingsChanged={(settings) =>
              setFilterSetttings(settings || null)
            }
            gridConfiguration={{
              ...props.gridConfiguration,
              toolbar: toolbarItems,
            }}
            showGridFilters={props.showGridFilters ?? true}
            allowSearch={props.allowSearch ?? true}
            predicate={props.predicate}
            gridHeightSettings={props.gridHeightSettings}
          />
        )}
      </>
    );

    return (
      <div className="primary-grid">
        {!currentGridView && (
          <div>
            <br />
            <SkeletonComponent height="15px"></SkeletonComponent>
          </div>
        )}
        {props.showInCardContainer ? (
          <PrimaryCard>{gridContent}</PrimaryCard>
        ) : (
          gridContent
        )}
      </div>
    );
  }
);

export const PrimaryGrid = PrimaryGridComponent;
export default PrimaryGrid;
